import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, Col, Row } from 'reactstrap';
import { Campaign, MediaResponse } from 'types';
import { useFeatures } from 'utils/util/features';
import InputAuxiliary from 'views/Widgets/Inputs/InputAuxiliary';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import {
  TICKETING_SYSTEMS_INPUT,
  TICKETING_SYSTEMS_LOADING_INPUT,
  useTicketingInformation,
} from './common';

interface AIBannerProps {
  className?: string;
  onSubmit: (object, onCallback, handleClose, handleError) => any;
  onCallback: (object) => void;
  campaign: Campaign;
  contextPersonId?: number;
}

const ActivitiesAIBanner: FC<AIBannerProps> = ({
  className = 'mb-4',
  onSubmit,
  onCallback,
  campaign,
  contextPersonId,
}) => {
  const { formatMessage } = useIntl();
  const { isGroupAndOptionEnabled } = useFeatures();
  const {
    ticketingSystems,
    loading: loadingTicketingSystems,
    fetch: fetchTicketingSystems,
  } = useTicketingInformation({
    campaign,
    contextPersonId,
    lazy: true,
  });

  const inputs = useMemo(() => {
    const enableAttachments = isGroupAndOptionEnabled(
      'ai_assistant',
      'ai_enable_attachments'
    );

    return [
      {
        type: INPUT_TYPES.CUSTOM_INPUT,
        component: (
          <Alert className="alert-light">
            <FormattedMessage
              id="app.views.a_i.activities_a_i_banner.ai_will_try"
              defaultMessage="
                    AI will try to generate a number of activities to
                    fullfill your remaining slots for this cycle.
                    Existing activities that you have already typed will not be overridden."
            />
          </Alert>
        ),
      },
      {
        name: 'user_prompt',
        helperText: formatMessage({
          id: 'app.views.a_i.activities_a_i_banner.auxiliary.inputs.user_prompt.helper_text',
          defaultMessage: 'Write any additional instructions for the AI.',
        }),
        label: formatMessage({
          id: 'app.views.a_i.activities_a_i_banner.auxiliary.inputs.user_prompt.label',
          defaultMessage: 'AI prompt (optional)',
        }),
        placeholder: formatMessage({
          id: 'app.views.a_i.activities_a_i_banner.auxiliary.inputs.user_prompt.placeholder',
          defaultMessage: 'Specific instructions or additional context.',
        }),
        type: INPUT_TYPES.TEXTAREA,
        maxLength: 1000,
        minRows: 3,
        maxRows: 15,
      },
      ...(enableAttachments
        ? [
            {
              name: 'user_media_file_descriptors',
              helperText: formatMessage({
                id: 'app.views.a_i.activities_a_i_banner.auxiliary.inputs.user_media_file_descriptions.helper_text',
                defaultMessage:
                  'To help the AI, attach any relevant documents, spreadsheets, or presentations.',
              }),
              label: formatMessage({
                id: 'app.views.a_i.activities_a_i_banner.auxiliary.inputs.user_media_file_descriptions.label',
                defaultMessage: 'Attach files',
              }),
              callToAction: formatMessage({
                id: 'app.views.a_i.activities_a_i_banner.auxiliary.inputs.user_media_file_descriptions.call_to_action',
                defaultMessage: 'Add documents, spreadsheets, or presentations',
              }),
              type: INPUT_TYPES.MEDIA_UPLOADER,
              multiple: true,
              mediaResponseMapper: (mediaResponse: MediaResponse) => ({
                id: mediaResponse.media.id,
                description: mediaResponse?.description,
              }),
              uploadUrl:
                process.env.REACT_APP_CONFIRM_API_URL + '/files/ai-file',
              maxFileSizeMb: 15,
            },
          ]
        : []),
      ...(loadingTicketingSystems
        ? [TICKETING_SYSTEMS_LOADING_INPUT()]
        : ticketingSystems.length > 0
          ? [TICKETING_SYSTEMS_INPUT(formatMessage, ticketingSystems)]
          : []),
    ];
  }, [
    formatMessage,
    isGroupAndOptionEnabled,
    loadingTicketingSystems,
    ticketingSystems,
  ]);

  return (
    <Alert className={`alert-light py-4 mb-0 ${className ?? ''}`}>
      <Row>
        <Col className="col-auto">
          <i className="fe fe-zap" />
        </Col>
        <Col>
          <Row>
            <Col className="ms-n3 mb-2 mb-md-0">
              <h4 className="mt-1 mb-1">
                <FormattedMessage
                  id="app.views.a_i.activities_a_i_banner.title"
                  defaultMessage="Let AI save you time"
                />
              </h4>
              <p className="mb-0">
                <FormattedMessage
                  id="app.views.a_i.activities_a_i_banner.sub_title"
                  defaultMessage="Generate activities in seconds, then make it your own."
                />
              </p>
            </Col>
          </Row>
        </Col>
        <Col className="col-12 col-md-auto mt-3 mt-md-0">
          <InputAuxiliary
            name="ai_banner"
            value=""
            buttonClassName="btn btn-primary"
            columnStyle="col-12"
            type="MODAL"
            buttonText={formatMessage({
              id: 'app.views.a_i.activities_a_i_banner.auxiliary.button_text',
              defaultMessage: 'Draft activities with AI ',
            })}
            title={formatMessage({
              id: 'app.views.a_i.activities_a_i_banner.auxiliary.title',
              defaultMessage: 'Let AI generate your activities',
            })}
            loaderText={formatMessage({
              id: 'app.views.a_i.activities_a_i_banner.auxiliary.loader_text',
              defaultMessage: 'Generating...',
            })}
            submitButtonTitle={formatMessage({
              id: 'app.views.a_i.activities_a_i_banner.auxiliary.submit_button_title',
              defaultMessage: 'Generate',
            })}
            onSubmit={onSubmit}
            onCallback={onCallback}
            onOpen={fetchTicketingSystems}
            inputs={inputs}
          />
        </Col>
      </Row>
    </Alert>
  );
};

export default ActivitiesAIBanner;
